"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { Menu, X, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LanguageToggle } from "@/components/LanguageToggle";
import { supabase } from "@/integrations/supabase/client";
import { clearCachedSubscription } from "@/hooks/useSubscription";

const logo = "/logo.png";

interface LandingNavProps {
  showSectionLinks?: boolean;
}

export function LandingNav({ showSectionLinks = true }: LandingNavProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const isLoginPage = pathname === "/login";
  const { t } = useTranslation('common');

  const handleLogout = async () => {
    clearCachedSubscription();
    await supabase.auth.signOut();
    setIsSignedIn(false);
    router.push('/');
  };

  useEffect(() => {
    // Set up listener FIRST to avoid missing auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsSignedIn(!!session);
    });
    
    // Then check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsSignedIn(!!session);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  return (
    <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link href="/">
            <img src={logo} alt="RUNNIQ Logo" className="h-12 w-auto" />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            {showSectionLinks && (
              <>
                <a href="/#features" className="text-sm uppercase hover:text-primary transition-colors">
                  {t('nav.features')}
                </a>
                <a href="/#how-it-works" className="text-sm uppercase hover:text-primary transition-colors">
                  {t('nav.howItWorks')}
                </a>
                <a href="/#pricing" className="text-sm uppercase hover:text-primary transition-colors">
                  {t('nav.pricing')}
                </a>
                <a href="/#faq" className="text-sm uppercase hover:text-primary transition-colors">
                  {t('nav.faq')}
                </a>
              </>
            )}
            <Link href="/plans" className="text-sm uppercase hover:text-primary transition-colors">
              {t('nav.trainingPlans')}
            </Link>
            <Link href="/tools" className="text-sm uppercase hover:text-primary transition-colors">
              {t('nav.freeTools')}
            </Link>
            <Link href="/about" className="text-sm uppercase hover:text-primary transition-colors">
              {t('nav.about')}
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <LanguageToggle />
            {isSignedIn ? (
              <>
                <Button asChild>
                  <Link href="/dashboard">{t('nav.openApp')}</Link>
                </Button>
                <Button variant="ghost" size="icon" onClick={handleLogout} title="Logout">
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                {!isLoginPage && (
                  <Button variant="ghost" asChild>
                    <Link href="/login">{t('nav.signIn')}</Link>
                  </Button>
                )}
                <Button asChild>
                  <Link href="/signup">{t('nav.startFreeTrial')}</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 md:hidden">
            <LanguageToggle />
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-4">
            {showSectionLinks && (
              <>
                <a href="/#features" className="block text-sm uppercase hover:text-primary transition-colors">
                  {t('nav.features')}
                </a>
                <a href="/#how-it-works" className="block text-sm uppercase hover:text-primary transition-colors">
                  {t('nav.howItWorks')}
                </a>
                <a href="/#pricing" className="block text-sm uppercase hover:text-primary transition-colors">
                  {t('nav.pricing')}
                </a>
                <a href="/#faq" className="block text-sm uppercase hover:text-primary transition-colors">
                  {t('nav.faq')}
                </a>
              </>
            )}
            <Link href="/plans" className="block text-sm uppercase hover:text-primary transition-colors">
              {t('nav.trainingPlans')}
            </Link>
            <Link href="/tools" className="block text-sm uppercase hover:text-primary transition-colors">
              {t('nav.freeTools')}
            </Link>
            <Link href="/about" className="block text-sm uppercase hover:text-primary transition-colors">
              {t('nav.about')}
            </Link>
            <div className="flex flex-col gap-2 pt-4">
              {isSignedIn ? (
                <>
                  <Button asChild>
                    <Link href="/dashboard">{t('nav.openApp')}</Link>
                  </Button>
                  <Button variant="ghost" onClick={handleLogout} className="justify-start">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  {!isLoginPage && (
                    <Button variant="ghost" asChild>
                      <Link href="/login">{t('nav.signIn')}</Link>
                    </Button>
                  )}
                  <Button asChild>
                    <Link href="/signup">{t('nav.startFreeTrial')}</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}